import React, { createContext, useContext, useEffect, useReducer, useState } from 'react'

import cloneDeep from 'lodash/cloneDeep'
import partition from 'lodash/partition'
import { createSelector } from 'reselect'

import ListingsContext from '@/context/listings'
import type { GlobalPriceRangeObject, ListingsStateType, RowMapboxIdsBySection } from '@/context/listings/types'
import { assignGroupMapAttributes, composeListingsGroups, getInitialGroupMaps } from '@/context/listings/utils'
import { ComposedListing, ListingsGroup } from '@/types/app-types'
import { trackProductionFilter } from '@/utils/analytics'
import { getHighestFilterPrice } from '@/utils/filters'

import { ProductionFilterStateLocalStorage } from '../../types'

import {
    Action,
    ActionType,
    ContextValue,
    CurrencyUpdatedAction,
    InitFilterAction,
    OnHighlightSectionAction,
    OnPriceFilterChangeAction,
    OnQuantityFilterChangeAction,
    OnSelectRowAction,
    OnSelectSectionAction,
    OnShowAllInPriceChangeAction,
    OnSortFilterChangeAction,
    OnTypeFilterChangeAction,
    ProductionFilterState,
    ResetFiltersAction,
    ResetFiltersExceptSectionsAction,
    ResetSectionAction,
    SectionDefinition,
    SectionRowsMap,
    SectionsPriceAndColor,
    SectionsRowsDataType,
    SelectSimilarAction,
    SelectSimilarSnackbarVisibilityAction,
    SortFilterNames,
    TypeFilterName,
} from './types'
import {
    checkModifiedFilter,
    filterListings,
    getHighestAndLowestPriceByShowAip,
    getProductionFilterLocalStorage,
    naturalSort,
    setProductionFilterLocalStorage,
    setProductionFilterLocalStorageById,
} from './utils'

export const defaultState = {
    typeFilterState: {
        superSeller: false,
        greatDeal: false,
        instantDelivery: false,
        recentlyViewed: false,
    },
    quantityFilterState: {
        quantity: 0,
    },
    sortFilterState: {
        name: SortFilterNames.PRICE,
    },
    priceFilterState: {
        currentMin: 0,
        currentMax: 0,
        currency: '',
    },
    selectedSectionsFilterState: [],
    selectedRowsFilterState: [],
    highlightedSectionsState: [],
    showAllInPrice: false,
    showSelectSimilarSnackbar: false,
    selectedGroups: [],
    modifiedFilterList: [],
    // whether a section was filtered directly through clicking on the map or the group chips
    hasSectionBeenManuallyFiltered: false,
    expiration: 0,
}

export const defaultContextValue: ContextValue = {
    state: defaultState,
    selectors: {
        filteredListingsSelector: [],
        getCurrentSortByValueSelector: 'price',
        getCurrentSortByButtonTextSelector: 'Sort By',
        filteredSections: [],
        filteredSectionsWithPriceAndColor: {},
        getViewResultsButtonTextSelector: 'No Results',
        getHighlightedSections: [],
        hasGreatDealsSelector: false,
        getLastSelectedSectionSelector: undefined,
        getHighlightedRows: [],
    },
    dispatch: {
        onTypeFilterChangeAction: () => undefined,
        onQuantityFilterChangeAction: () => undefined,
        onSortFilterChangeAction: () => undefined,
        onPriceFilterChangeAction: () => undefined,
        onFilterResetAction: () => undefined,
        onFilterResetExceptSectionsAction: () => undefined,
        onSectionSelectAction: () => undefined,
        onSectionHighlightAction: () => undefined,
        onShowAllInPriceChangeAction: () => undefined,
        onResetSectionSelectionAction: () => undefined,
        onShowSimilarSectionsAction: () => undefined,
        onSetSelectSimilarSnackbarVisibility: () => undefined,
        onCurrencyUpdated: () => undefined,
        onRowSelectAction: () => undefined,
    },
}

const initFilterAction = (
    dispatch: React.Dispatch<InitFilterAction>,
    payload: { initialFilter: ProductionFilterStateLocalStorage | null; currentProductionId: number; showAip: boolean },
) => {
    dispatch({ type: ActionType.INIT_FILTER, payload })
}

const filterResetAction = (dispatch: React.Dispatch<ResetFiltersAction>) => {
    dispatch({ type: ActionType.RESET_FILTERS })
}

const filterResetExceptSectionsAction = (dispatch: React.Dispatch<ResetFiltersExceptSectionsAction>) => {
    dispatch({ type: ActionType.RESET_FILTERS_EXCEPT_SECTIONS })
}

const typeFilterChangeAction = (
    dispatch: React.Dispatch<OnTypeFilterChangeAction>,
    payload: { filterName: TypeFilterName; filterValue: boolean },
) => {
    dispatch({ type: ActionType.ON_TYPE_FILTER_CHANGE, payload: payload })
}

const quantityFilterChangeAction = (
    dispatch: React.Dispatch<OnQuantityFilterChangeAction>,
    payload: { quantity: number },
) => {
    dispatch({ type: ActionType.ON_QUANTITY_FILTER_CHANGE, payload: payload })
}

const sortFilterChangeAction = (
    dispatch: React.Dispatch<OnSortFilterChangeAction>,
    payload: { name: SortFilterNames },
) => {
    dispatch({ type: ActionType.ON_SORT_FILTER_CHANGE, payload: payload })
}

const priceFilterChangeAction = (
    dispatch: React.Dispatch<OnPriceFilterChangeAction>,
    payload: { field: string; value: number; shouldTrackFilterChange: boolean },
) => {
    dispatch({ type: ActionType.ON_PRICE_FILTER_CHANGE, payload: payload })
}

const sectionSelectFilterChangeAction = (
    dispatch: React.Dispatch<OnSelectSectionAction>,
    payload: SectionDefinition,
) => {
    dispatch({ type: ActionType.ON_SELECT_SECTION, payload: payload })
}

const rowSelectFilterChangeAction = (dispatch: React.Dispatch<OnSelectRowAction>, payload: { rowId: string }) => {
    dispatch({ type: ActionType.ON_SELECT_ROW, payload: payload })
}

const sectionSelectionResetAction = (dispatch: React.Dispatch<ResetSectionAction>) => {
    dispatch({ type: ActionType.RESET_SECTION_SELECTION })
}

const sectionHighlightAction = (dispatch: React.Dispatch<OnHighlightSectionAction>, payload: SectionDefinition) => {
    dispatch({ type: ActionType.ON_HIGHLIGHT_SECTION, payload: payload })
}

const showAllInPriceChangeAction = (dispatch: React.Dispatch<OnShowAllInPriceChangeAction>) => {
    dispatch({ type: ActionType.ON_SHOW_ALL_IN_PRICE_CHANGE })
}

const showSimilarSectionsAction = (
    dispatch: React.Dispatch<SelectSimilarAction>,
    payload?: { groupId: string; selected: boolean },
) => {
    dispatch({ type: ActionType.ON_SELECT_SIMILAR_SECTION, payload })
}

const setSelectSimilarSnackbarVisibilityAction = (
    dispatch: React.Dispatch<SelectSimilarSnackbarVisibilityAction>,
    payload: { showSnackbar: boolean },
) => {
    dispatch({ type: ActionType.ON_SET_SHOW_SIMILAR_SNACKBAR_VISIBILITY, payload })
}

const currencyUpdatedAction = (
    dispatch: React.Dispatch<CurrencyUpdatedAction>,
    payload: { showAip: boolean; currency: string },
) => {
    dispatch({ type: ActionType.ON_CURRENCY_UPDATED, payload })
}

const filterContext = createContext(defaultContextValue)

const { Provider } = filterContext

const useFilterReducer = (
    globalPriceObject: GlobalPriceRangeObject,
    listingsState: ListingsStateType,
    rowMapboxIdsBySection: RowMapboxIdsBySection,
): [ProductionFilterState, React.Dispatch<Action>] => {
    const defaultClone = cloneDeep(defaultState)

    return useReducer((state: ProductionFilterState, action: Action) => {
        const listingsShowAip = listingsState?.data?.global[0]?.showAip === 'true'
        const highPrice = getHighestFilterPrice(globalPriceObject, state.showAllInPrice)
        const lowPrice = state.showAllInPrice ? globalPriceObject.lowestAllInPrice : globalPriceObject.lowestPrice
        const sections = listingsState.data?.sections
        let dataClone = { ...state }

        switch (action.type) {
            case ActionType.ON_SET_SHOW_SIMILAR_SNACKBAR_VISIBILITY: {
                const { showSnackbar } = action.payload

                // don't show snackbar with nothing selected
                if (showSnackbar && dataClone.selectedSectionsFilterState.length < 1) {
                    return dataClone
                }

                const lastSelectedSectionId = dataClone.selectedSectionsFilterState.at(-1)?.id
                if (!lastSelectedSectionId) {
                    return dataClone
                }

                const groupId = getSectionGroupHashmapSelector(listingsState).get(lastSelectedSectionId)
                // don't show snackbar if there's only 1 section in the group
                if (!groupId || !groupHasMoreThanOneSectionSelector(listingsState, groupId)) {
                    return dataClone
                }

                // don't show snackbar if last selection fully selects the group (there'd be nothing left to select)
                if (dataClone.selectedGroups.includes(groupId)) {
                    return dataClone
                }

                dataClone.showSelectSimilarSnackbar = showSnackbar
                return dataClone
            }
            case ActionType.ON_SELECT_SIMILAR_SECTION:
                // This action by default picks the group of the last highlighted section (via see similar snackbar),
                // but we can also explicitly select or deselect groups (via group chips) by giving it an optional payload
                let groupId: string | undefined
                const isSelectingGroup = action.payload?.selected ?? true

                if (action.payload?.groupId && action.payload.groupId.trim() !== '') {
                    groupId = action.payload?.groupId
                } else {
                    const lastSelectedSectionId = dataClone.selectedSectionsFilterState.at(-1)?.id
                    // get group last selected section belongs to
                    // can't use an empty string here, as there are groups with empty string names sometimes
                    groupId = getSectionGroupHashmapSelector(listingsState).get(lastSelectedSectionId ?? '__NO_GROUP__')
                }

                if (!groupId) {
                    return dataClone
                }

                if (isSelectingGroup) {
                    dataClone.selectedGroups = [...dataClone.selectedGroups, groupId]

                    const sectionIdsInGroup =
                        listingsState.data?.sections
                            ?.filter(
                                (section) =>
                                    section.g === groupId &&
                                    // stop duplicate sections from being selected
                                    !dataClone.selectedSectionsFilterState.some(({ id }) => id === section.i) &&
                                    // don't select sections with no inventory
                                    Number(section.q) > 0,
                            )
                            .map(({ i }) => ({ id: i })) ?? []

                    dataClone.selectedSectionsFilterState = [
                        ...dataClone.selectedSectionsFilterState,
                        ...sectionIdsInGroup,
                    ]

                    dataClone.hasSectionBeenManuallyFiltered = true
                } else {
                    // deselecting a group
                    dataClone.selectedGroups = dataClone.selectedGroups.filter((group) => group !== groupId)

                    const sectionIdsInGroup = listingsState.data?.sections
                        ?.filter((section) => section.g === groupId)
                        .map(({ i }) => ({ id: i }))

                    dataClone.selectedSectionsFilterState = dataClone.selectedSectionsFilterState.filter(
                        ({ id }) => !sectionIdsInGroup?.some((section) => section.id === id),
                    )
                    dataClone.hasSectionBeenManuallyFiltered = false
                }

                dataClone.showSelectSimilarSnackbar = false

                return dataClone
            case ActionType.ON_HIGHLIGHT_SECTION:
                // If this action is fired with an empty string as the ID, reset highlighted state
                // For example, backButtonHandler in components/ticket-details
                if (action.payload.id === '') {
                    dataClone.highlightedSectionsState = []
                }
                // If value exists in state already, remove it. Otherwise, add it
                else if (dataClone.highlightedSectionsState.find(({ id }) => id === action.payload.id)) {
                    dataClone.highlightedSectionsState = dataClone.highlightedSectionsState.filter(
                        ({ id }) => id !== action.payload.id,
                    )
                } else {
                    dataClone.highlightedSectionsState = [
                        ...dataClone.highlightedSectionsState,
                        { id: action.payload.id },
                    ]
                }
                return dataClone
            case ActionType.ON_SELECT_SECTION:
                const isSectionSelected = !!dataClone.selectedSectionsFilterState.find(
                    ({ id }) => id === action.payload.id,
                )

                // If a section is already selected but not directly through clicking on the map
                // or the group chips, don't deselect it
                if (isSectionSelected && !action.payload.hasSectionBeenManuallyFiltered) {
                    return dataClone
                }

                // If value exists in state already, remove it. Otherwise, add it
                if (isSectionSelected) {
                    dataClone.selectedSectionsFilterState = dataClone.selectedSectionsFilterState.filter(
                        ({ id }) => id !== action.payload.id,
                    )

                    // if we're deselecting one section from a group, de-activate that group chip
                    // as its no longer selected
                    if (dataClone.selectedGroups.length > 0) {
                        const groupId = listingsState?.data?.sections?.find(
                            (section) => section.i === action.payload.id,
                        )?.g

                        dataClone.selectedGroups = dataClone.selectedGroups.filter((group) => group !== groupId)
                    }

                    // clear any row filters that are already present for this section
                    const allRowsInSection = rowMapboxIdsBySection[action.payload.id]
                    dataClone.selectedRowsFilterState = [...dataClone.selectedRowsFilterState].filter(
                        (r) => !allRowsInSection.includes(r),
                    )
                    dataClone.hasSectionBeenManuallyFiltered = false
                } else {
                    // Changing how this works (adding a section to the end of the array)
                    // will break ON_SELECT_SIMILAR_SECTION, as that uses the last item in the array
                    dataClone.selectedSectionsFilterState = [
                        ...dataClone.selectedSectionsFilterState,
                        { id: action.payload.id },
                    ]

                    // check if each item in the group is selected, and if so highlight the chip
                    const sectionGroupMap = getSectionGroupHashmapSelector(listingsState)
                    const sectionGroupId = sectionGroupMap.get(action.payload.id)

                    const allSectionsInGroup = getAllSectionsWithInventoryInGroupSelector(listingsState, sectionGroupId)

                    // Using a set incase there's a duplicate selection, and to avoid nested looping.
                    // Since this happens each time a user clicks a section, we'll want the performance savings.
                    const selectedSectionsInGroupSet = new Set(
                        dataClone.selectedSectionsFilterState
                            .filter(({ id }) => sectionGroupMap.get(id) === sectionGroupId)
                            .map(({ id }) => id),
                    )

                    if (
                        sectionGroupId &&
                        selectedSectionsInGroupSet.size > 0 &&
                        allSectionsInGroup.every((section) => selectedSectionsInGroupSet.has(section))
                    ) {
                        dataClone.selectedGroups = [...dataClone.selectedGroups, sectionGroupId]
                    }

                    dataClone.hasSectionBeenManuallyFiltered = action.payload.hasSectionBeenManuallyFiltered
                }
                dataClone.highlightedSectionsState = []
                trackProductionFilter(dataClone, listingsState)
                return dataClone
            case ActionType.ON_SELECT_ROW:
                const sectionMbiWithRow = action.payload.rowId
                const [payloadSectionMbi] = sectionMbiWithRow.split(':')
                const selectedSectionId = sections?.find((section) => section.mbi === payloadSectionMbi)?.i ?? ''
                const isRowSelected = dataClone.selectedRowsFilterState!.some((id) => id === sectionMbiWithRow)
                const allRowsInSection = rowMapboxIdsBySection[selectedSectionId]
                const isRowSectionSelected = dataClone.selectedSectionsFilterState.some(
                    (currentSelectedSection) => currentSelectedSection.id === selectedSectionId,
                )
                const isSelectedRowOrHasSelectedSection = isRowSelected || isRowSectionSelected

                if (isSelectedRowOrHasSelectedSection) {
                    // clear the row filter state if row is selected through row filter
                    if (isRowSelected) {
                        dataClone.selectedRowsFilterState = dataClone.selectedRowsFilterState
                            .filter((id) => id !== sectionMbiWithRow)
                            .filter(Boolean)
                    }
                    // if we're deselecting a row from a selected section clear section selection and select all other rows
                    if (isRowSectionSelected) {
                        // remove section filter for section row belongs to
                        dataClone.selectedSectionsFilterState = dataClone.selectedSectionsFilterState.filter(
                            (filterSelectedSection) => filterSelectedSection.id !== selectedSectionId,
                        )
                        // select all other rows in the section besides the one selected
                        const allRowsInSectionExceptDeselected = allRowsInSection?.filter(
                            (r) => r !== sectionMbiWithRow,
                        )
                        dataClone.selectedRowsFilterState = [
                            ...dataClone.selectedRowsFilterState,
                            ...allRowsInSectionExceptDeselected,
                        ]
                    }
                } else {
                    dataClone.selectedRowsFilterState = [
                        ...dataClone.selectedRowsFilterState,
                        action.payload.rowId,
                    ].filter(Boolean)

                    const isAllRowsSelected = allRowsInSection.every((r) =>
                        dataClone.selectedRowsFilterState.includes(r),
                    )
                    // if all rows are selected we want to clear the selected rows within the section
                    if (isAllRowsSelected) {
                        dataClone.selectedRowsFilterState = dataClone.selectedRowsFilterState.filter(
                            (selectedRow) => !allRowsInSection.includes(selectedRow),
                        )
                        // now we want to filter by the section instead of row level
                        if (selectedSectionId) {
                            dataClone.selectedSectionsFilterState = [
                                ...dataClone.selectedSectionsFilterState,
                                { id: selectedSectionId },
                            ]
                        }
                    }
                }
                trackProductionFilter(dataClone, listingsState)
                return {
                    ...dataClone,
                    modifiedFilterList: checkModifiedFilter(dataClone, highPrice, lowPrice, listingsShowAip),
                }
            case ActionType.ON_TYPE_FILTER_CHANGE:
                switch (action.payload.filterName.name) {
                    case 'superSeller':
                        dataClone.typeFilterState.superSeller = action.payload.filterValue
                        break
                    case 'greatDeal':
                        dataClone.typeFilterState.greatDeal = action.payload.filterValue
                        break
                    case 'recentlyViewed':
                        dataClone.typeFilterState.recentlyViewed = action.payload.filterValue
                        break
                    default:
                        dataClone.typeFilterState.instantDelivery = action.payload.filterValue
                        break
                }
                trackProductionFilter(dataClone, listingsState)
                return {
                    ...dataClone,
                    modifiedFilterList: checkModifiedFilter(dataClone, highPrice, lowPrice, listingsShowAip),
                }
            case ActionType.ON_QUANTITY_FILTER_CHANGE:
                dataClone.quantityFilterState.quantity = action.payload.quantity
                trackProductionFilter(dataClone, listingsState)
                return {
                    ...dataClone,
                    modifiedFilterList: checkModifiedFilter(dataClone, highPrice, lowPrice, listingsShowAip),
                }
            case ActionType.ON_SORT_FILTER_CHANGE:
                dataClone.sortFilterState.name = action.payload.name
                trackProductionFilter(dataClone, listingsState)
                const modifiedFilterList = checkModifiedFilter(dataClone, highPrice, lowPrice, listingsShowAip)
                return { ...dataClone, modifiedFilterList }
            case ActionType.ON_PRICE_FILTER_CHANGE:
                switch (action.payload.field) {
                    case 'currentMax':
                        dataClone.priceFilterState.currentMax = action.payload.value
                        break
                    case 'currentMin':
                        dataClone.priceFilterState.currentMin = action.payload.value
                        break
                }

                if (action.payload.shouldTrackFilterChange) {
                    trackProductionFilter(dataClone, listingsState)
                }
                return {
                    ...dataClone,
                    modifiedFilterList: checkModifiedFilter(dataClone, highPrice, lowPrice, listingsShowAip),
                }
            case ActionType.ON_CURRENCY_UPDATED:
                let high = highPrice
                let low = lowPrice

                if (
                    action.payload.currency !== dataClone.priceFilterState.currency ||
                    action.payload.showAip !== dataClone.showAllInPrice
                ) {
                    dataClone.priceFilterState.currency = action.payload.currency

                    if (action.payload.showAip) {
                        dataClone.showAllInPrice = true
                        dataClone.priceFilterState.currentMin = globalPriceObject.lowestAllInPrice
                        dataClone.priceFilterState.currentMax = getHighestFilterPrice(globalPriceObject, true)
                    } else {
                        dataClone.priceFilterState.currentMin = globalPriceObject.lowestPrice
                        dataClone.priceFilterState.currentMax = getHighestFilterPrice(globalPriceObject, false)
                    }
                    // If show all in price has changed or the currency has changed then pass in the dataClone values
                    // for high and low because we don't want priceRange to be in the modifiedFilterList
                    high = dataClone.priceFilterState.currentMax
                    low = dataClone.priceFilterState.currentMin
                }

                return {
                    ...dataClone,
                    modifiedFilterList: checkModifiedFilter(dataClone, high, low, listingsShowAip),
                }
            case ActionType.ON_SHOW_ALL_IN_PRICE_CHANGE:
                dataClone.showAllInPrice = !dataClone.showAllInPrice
                if (dataClone.showAllInPrice) {
                    dataClone.priceFilterState.currentMin = globalPriceObject.lowestAllInPrice
                    dataClone.priceFilterState.currentMax = getHighestFilterPrice(globalPriceObject, true)
                } else {
                    dataClone.priceFilterState.currentMin = globalPriceObject.lowestPrice
                    dataClone.priceFilterState.currentMax = getHighestFilterPrice(globalPriceObject, false)
                }

                return {
                    ...dataClone,
                    modifiedFilterList: checkModifiedFilter(
                        dataClone,
                        // Pass in current dataClone values for high and low because we don't want priceRange
                        // to be in the modifiedFilterList when we're just checking/unchecking show all in price
                        dataClone.priceFilterState.currentMax,
                        dataClone.priceFilterState.currentMin,
                        listingsShowAip,
                    ),
                }
            case ActionType.RESET_FILTERS:
                defaultClone.showAllInPrice = listingsShowAip
                defaultClone.priceFilterState.currency = dataClone.priceFilterState.currency as string
                if (globalPriceObject) {
                    if (defaultClone.showAllInPrice) {
                        defaultClone.priceFilterState.currentMax = getHighestFilterPrice(globalPriceObject, true)
                        defaultClone.priceFilterState.currentMin = globalPriceObject.lowestAllInPrice
                    } else {
                        defaultClone.priceFilterState.currentMax = getHighestFilterPrice(globalPriceObject)
                        defaultClone.priceFilterState.currentMin = globalPriceObject.lowestPrice
                    }
                }
                trackProductionFilter(defaultClone, listingsState)
                return defaultClone // ! Intentionally manually return for this case

            case ActionType.RESET_FILTERS_EXCEPT_SECTIONS:
                const selectedSections = dataClone.selectedSectionsFilterState
                const currency = dataClone.priceFilterState.currency
                dataClone = defaultClone
                dataClone.selectedSectionsFilterState = selectedSections
                dataClone.showAllInPrice = listingsShowAip
                dataClone.priceFilterState.currency = currency

                if (globalPriceObject) {
                    if (dataClone.showAllInPrice) {
                        dataClone.priceFilterState.currentMax = getHighestFilterPrice(globalPriceObject, true)
                        dataClone.priceFilterState.currentMin = globalPriceObject.lowestAllInPrice
                    } else {
                        dataClone.priceFilterState.currentMax = getHighestFilterPrice(globalPriceObject)
                        dataClone.priceFilterState.currentMin = globalPriceObject.lowestPrice
                    }
                }
                trackProductionFilter(dataClone, listingsState)
                return dataClone

            case ActionType.RESET_SECTION_SELECTION:
                dataClone.selectedSectionsFilterState = []
                dataClone.selectedGroups = []
                dataClone.selectedRowsFilterState = []
                dataClone.hasSectionBeenManuallyFiltered = false
                trackProductionFilter(dataClone, listingsState)

                return {
                    ...dataClone,
                    modifiedFilterList: checkModifiedFilter(dataClone, highPrice, lowPrice, listingsShowAip),
                }

            case ActionType.INIT_FILTER:
                setProductionFilterLocalStorage(action.payload.initialFilter)
                const { initialFilter, currentProductionId, showAip } = action.payload
                const { highestPrice, lowestPrice } = getHighestAndLowestPriceByShowAip(globalPriceObject, showAip)

                const initialMaxPriceFilter =
                    initialFilter?.[currentProductionId]?.priceFilterState?.currentMax || Number.MAX_SAFE_INTEGER
                const initialMinPriceFilter = initialFilter?.[currentProductionId]?.priceFilterState?.currentMin || 0

                const shouldApplyMaxPrice = initialMaxPriceFilter < highestPrice
                const shouldApplyMinPrice = initialMinPriceFilter > lowestPrice
                const currentMax = shouldApplyMaxPrice ? initialMaxPriceFilter : highestPrice
                const currentMin = shouldApplyMinPrice ? initialMinPriceFilter : lowestPrice

                const initializedFilter: ProductionFilterState = {
                    ...defaultClone, // use this instead of `defaultState`, it somehow mutates it and will cause MAJOR headache
                    ...(initialFilter?.[currentProductionId] ?? {}),
                    priceFilterState: {
                        currentMax,
                        currentMin,
                        currency: initialFilter?.[currentProductionId]?.priceFilterState.currency || '',
                    },
                }
                dataClone = { ...dataClone, ...initializedFilter }

                return {
                    ...dataClone,
                    modifiedFilterList: checkModifiedFilter(dataClone, highPrice, lowPrice, listingsShowAip),
                }
        }
    }, defaultClone)
}

const rowMapboxIdsBySectionSelector = createSelector(
    (listings: (ComposedListing | ListingsGroup)[]) => ({
        listings,
    }),
    ({ listings }) => {
        const sectionRows: RowMapboxIdsBySection = {}
        listings.forEach((listing) => {
            const rowId = listing.row.toLowerCase()
            if (!listing.mbi || !rowId || !listing.sectionId) return
            const mapboxRowId = `${listing.mbi}:${rowId}`
            if (sectionRows[listing.sectionId]) {
                if (sectionRows[listing.sectionId].includes(mapboxRowId)) return
                sectionRows[listing.sectionId].push(mapboxRowId)
            } else {
                sectionRows[listing.sectionId] = [mapboxRowId]
            }
        })
        return sectionRows
    },
)

export const filteredListingsSelectorMap = createSelector(
    (
        listings: (ComposedListing | ListingsGroup)[],
        state: ProductionFilterState,
        priceRangeObject: GlobalPriceRangeObject,
    ) => ({
        listings,
        state,
        priceRangeObject,
    }),
    ({ listings, state, priceRangeObject }) => {
        const [listingsGroups, composedListings] = partition(listings, ({ isGroup }) => !!isGroup)
        const sectionRows: Record<ComposedListing['sectionId'], ComposedListing['row'][]> = {}
        listings.forEach((listing) => {
            const rowId = listing.row.toLowerCase()
            if (sectionRows[listing.sectionId]) {
                if (sectionRows[listing.sectionId].includes(rowId)) return
                sectionRows[listing.sectionId].push(rowId)
            } else {
                sectionRows[listing.sectionId] = [rowId]
            }
        })

        const filteredComposedListings = filterListings(composedListings, state, priceRangeObject)
        let filteredListingsGroups = listingsGroups as ListingsGroup[]

        if (filteredListingsGroups.length > 0) {
            const groupMaps = getInitialGroupMaps(filteredListingsGroups)
            filteredComposedListings
                .filter(({ inGroup }) => !!inGroup)
                .sort((a, b) => a.price - b.price)
                .forEach((listing) => assignGroupMapAttributes(groupMaps[listing.groupId], listing))
            filteredListingsGroups = composeListingsGroups(groupMaps).filter(
                (listingsGroup) => listingsGroup.quantityOptionsList.length > 0,
            )
        }

        const { sortFilterState } = state

        let filteredListings: (ComposedListing | ListingsGroup)[] = [
            ...filteredListingsGroups,
            ...filteredComposedListings,
        ]

        //  Sort listings in the way a user has selected
        if (sortFilterState.name === SortFilterNames.PRICE) {
            // sort by price lowest to highest
            filteredListings = filteredListings.sort((a, b) => a.price - b.price)
        }
        if (sortFilterState.name === SortFilterNames.PRICE_HIGH) {
            // sort by price highest to lowest
            filteredListings = filteredListings.sort((a, b) => b.price - a.price)
        }
        if (sortFilterState.name === SortFilterNames.ROW) {
            // sort by row lowest to highest
            filteredListings = filteredListings.sort((a, b) =>
                naturalSort(a.row.toLowerCase(), b.row.toLowerCase(), true),
            )
        }
        if (sortFilterState.name === SortFilterNames.SECTION) {
            // sort by section lowest to highest
            filteredListings = filteredListings.sort((a, b) =>
                naturalSort(a.section.toLowerCase(), b.section.toLowerCase()),
            )
        }

        // Sort unmapped sections/groups to bottom
        const unmappedSortFunction = (listing: ComposedListing | ListingsGroup) =>
            Number(listing.sectionId && listing.groupId)
        const [normalMappedSections, unmappedSections] = partition(
            filteredListings,
            (listing: ComposedListing | ListingsGroup) => unmappedSortFunction(listing),
        )

        const sectionRowsData = listings.reduce((acc: SectionsRowsDataType, currentListing: ComposedListing) => {
            if (!acc[currentListing.sectionId]) acc[currentListing.sectionId] = {}
            const section = acc[currentListing.sectionId]
            if (section[currentListing.row]) {
                if (section[currentListing.row].minPrice > currentListing.price) {
                    section[currentListing.row].minPrice = currentListing.price
                }
            } else section[currentListing.row] = { minPrice: currentListing.price }
            return acc
        }, {})

        normalMappedSections.forEach((section) => {
            section.availableRows = sectionRows[section.sectionId]
            section.availableRowsData = sectionRowsData[section.sectionId]
        })

        unmappedSections.forEach((section) => {
            section.availableRows = sectionRows[section.sectionId]
            section.availableRowsData = sectionRowsData[section.sectionId]
        })

        filteredListings = [...normalMappedSections, ...unmappedSections]

        return { listings: filteredListings, state }
    },
)

const selectedRowsSelector = createSelector(filteredListingsSelectorMap, ({ state }) => {
    const { selectedRowsFilterState } = state
    return selectedRowsFilterState.reduce<SectionRowsMap>((accumulator: SectionRowsMap, selectedRow: string) => {
        if (selectedRow) {
            const [section, row] = selectedRow.split(':')
            if (!accumulator[section]) {
                accumulator[section] = [row]
            } else {
                accumulator[section].push(row)
            }
        }
        return accumulator
    }, {})
})

export const filteredListingsSelector = createSelector(
    filteredListingsSelectorMap,
    selectedRowsSelector,
    ({ listings, state }, selectedRowsMap) => {
        const { selectedSectionsFilterState, selectedRowsFilterState } = state
        if (selectedSectionsFilterState.length || selectedRowsFilterState.length) {
            return listings.filter(
                (listing) =>
                    !listing.inGroup &&
                    (state.selectedSectionsFilterState.some(({ id }) => id === listing.sectionId) ||
                        selectedRowsMap?.[listing.mbi ?? '']?.includes?.(listing.row.toLowerCase())),
            )
        } else {
            return listings.filter((listing) => !listing.inGroup)
        }
    },
)

const filteredSectionsWithPriceAndColor = createSelector(filteredListingsSelectorMap, ({ listings }) => {
    return listings.reduce((acc, item) => {
        if (!acc[item.sectionId] || acc[item.sectionId].price > item.price) {
            acc[item.sectionId] = {
                ...item,
                price: item.price,
                allInPrice: item.allInPrice ? item.allInPrice : 0,
                color: item.sectionColor,
                row: item.row,
                section: item.section,
                availableRows: item.availableRows,
            }
        }
        return acc
    }, {} as SectionsPriceAndColor)
})

const filteredSections = createSelector(filteredSectionsWithPriceAndColor, (filteredSectionsWithPriceAndColor) => {
    return Object.keys(filteredSectionsWithPriceAndColor)
})

export const getCurrentSortByValueSelector = createSelector(
    (state: ProductionFilterState) => state.sortFilterState.name,
    (name) => {
        switch (name) {
            case SortFilterNames.SECTION:
                return 'section'
            case SortFilterNames.ROW:
                return 'row'
            case SortFilterNames.PRICE:
                return 'price'
            case SortFilterNames.PRICE_HIGH:
                return 'priceHigh'
            default:
                return 'price'
        }
    },
)

export const getHighlightedSections = createSelector(
    (state: ProductionFilterState) => ({
        selected: state.selectedSectionsFilterState.map(({ id }) => id),
        highlighted: state.highlightedSectionsState.map(({ id }) => id),
    }),
    ({ selected, highlighted }) => (highlighted.length > 0 ? highlighted : selected.length > 0 ? selected : []),
)

export const getHighlightedRows = createSelector(
    (state: ProductionFilterState) => {
        const result = state.selectedRowsFilterState.map((selectedSectionRow) => selectedSectionRow)
        return {
            selected: result,
            highlighted: result,
        }
    },
    ({ selected, highlighted }) => (highlighted.length > 0 ? highlighted : selected.length > 0 ? selected : []),
)

export const getCurrentSortByButtonTextSelector = createSelector(
    (state: ProductionFilterState) => state.sortFilterState.name,
    (name) => {
        switch (name) {
            case SortFilterNames.SECTION:
                return 'Sort by Section'
            case SortFilterNames.ROW:
                return 'Sort by Row'
            case SortFilterNames.PRICE:
                return 'Sort by Price'
            default:
                return 'Sort by Price'
        }
    },
)

const getViewResultsButtonTextSelector = createSelector(
    (filteredListings: (ComposedListing | ListingsGroup)[]) => filteredListings,
    (filteredListings) => {
        const numberOfFilteredListings = filteredListings.length
        if (numberOfFilteredListings > 0) {
            return `View ${numberOfFilteredListings} Result${numberOfFilteredListings !== 1 ? 's' : ''}`
        }
        return 'No Results'
    },
)

export const hasGreatDealsSelector = createSelector(
    (listings: (ComposedListing | ListingsGroup)[]) => ({ listings }),
    ({ listings }) => listings.some((listing: ComposedListing | ListingsGroup) => listing.isGreatDeal),
)

export const getLastSelectedSectionSelector = createSelector(
    (state: ProductionFilterState) => state.selectedSectionsFilterState,
    (sections: SectionDefinition[]) => sections.at(-1),
)

// Memoized so that we can avoid constant looping through every section
const groupHasMoreThanOneSectionSelector = createSelector(
    (listingsState: ListingsStateType, groupId: string) => ({ groupId, listings: listingsState.data?.sections }),
    ({ groupId, listings }) => (listings ?? [])?.filter(({ g, q }) => g === groupId && Number(q) > 0).length > 1,
)

// Maps section ID's to their group Id
const getSectionGroupHashmapSelector = createSelector(
    (listingsState: ListingsStateType) => ({ sections: listingsState.data?.sections }),
    ({ sections }) => {
        const sectionMap = new Map<string, string>()

        if (!sections) {
            return sectionMap
        }
        // looping through listings is expensive, for...of loop is much faster than forEach
        for (const section of sections) {
            sectionMap.set(section.i, section.g)
        }

        return sectionMap
    },
)

const getAllSectionsWithInventoryInGroupSelector = createSelector(
    (listingsState: ListingsStateType, groupId: string | undefined) => ({ listingsState, groupId }),
    ({ listingsState, groupId }) => {
        if (!groupId || !listingsState.data?.sections) {
            return []
        }

        return listingsState.data?.sections.filter(({ g, q }) => g === groupId && Number(q) > 0).map(({ i }) => i)
    },
)

export const FilterProvider: React.FC = ({ children }) => {
    const {
        state: listingsState,
        selectors: listingsSelectors,
        fetching: isListingFetching,
    } = useContext(ListingsContext)
    const { composedListings, globalPriceRangeObject, showAip, i18nListingsData } = listingsSelectors
    const [isInitialized, setIsInitialized] = useState(false)
    const rowMapboxIdsBySection = rowMapboxIdsBySectionSelector(composedListings)
    const [state, dispatch] = useFilterReducer(globalPriceRangeObject, listingsState, rowMapboxIdsBySection)
    const currentProductionId = Number(listingsState.data?.global[0]?.productionId)

    // Init filter setting with local stored setting
    useEffect(() => {
        if (!currentProductionId || isInitialized || isListingFetching) return

        const storedFilter = getProductionFilterLocalStorage()
        initFilterAction(dispatch, {
            currentProductionId,
            initialFilter: storedFilter,
            showAip,
        })

        setIsInitialized(true)
    }, [isInitialized, isListingFetching])

    useEffect(() => {
        // retrigger filter re-initilization on production ID change
        if (isInitialized) setIsInitialized(false)
    }, [currentProductionId])

    // Whenever filter `state` changes, update the localstorage
    useEffect(() => {
        if (!currentProductionId || !isInitialized) return
        setProductionFilterLocalStorageById(currentProductionId, state, globalPriceRangeObject, showAip)
    }, [state, isInitialized])

    useEffect(() => {
        if (i18nListingsData?.to) {
            // Update the currentMin and currentMax when the user selects a different currency.
            // This also works when isLocalizedCurrencyFeatureEnabled is off and we just use USD.
            currencyUpdatedAction(dispatch, { showAip, currency: i18nListingsData?.to })
        }
    }, [i18nListingsData?.to])

    return (
        <Provider
            value={{
                state,
                selectors: {
                    filteredListingsSelector: filteredListingsSelector(composedListings, state, globalPriceRangeObject),
                    getCurrentSortByValueSelector: getCurrentSortByValueSelector(state),
                    getCurrentSortByButtonTextSelector: getCurrentSortByButtonTextSelector(state),
                    filteredSectionsWithPriceAndColor: filteredSectionsWithPriceAndColor(
                        composedListings,
                        state,
                        globalPriceRangeObject,
                    ),
                    filteredSections: filteredSections(composedListings, state, globalPriceRangeObject),
                    getViewResultsButtonTextSelector: getViewResultsButtonTextSelector(
                        filteredListingsSelector(composedListings, state, globalPriceRangeObject),
                    ),
                    getHighlightedSections: getHighlightedSections(state),
                    hasGreatDealsSelector: hasGreatDealsSelector(composedListings),
                    getLastSelectedSectionSelector: getLastSelectedSectionSelector(state),
                    getHighlightedRows: getHighlightedRows(state),
                },
                dispatch: {
                    onTypeFilterChangeAction: (payload) => typeFilterChangeAction(dispatch, payload),
                    onQuantityFilterChangeAction: (payload) => quantityFilterChangeAction(dispatch, payload),
                    onSortFilterChangeAction: (payload) => sortFilterChangeAction(dispatch, payload),
                    onPriceFilterChangeAction: (payload) => priceFilterChangeAction(dispatch, payload),
                    onFilterResetAction: () => filterResetAction(dispatch),
                    onFilterResetExceptSectionsAction: () => filterResetExceptSectionsAction(dispatch),
                    onSectionSelectAction: (payload) => sectionSelectFilterChangeAction(dispatch, payload),
                    onSectionHighlightAction: (payload) => sectionHighlightAction(dispatch, payload),
                    onShowAllInPriceChangeAction: () => showAllInPriceChangeAction(dispatch),
                    onResetSectionSelectionAction: () => sectionSelectionResetAction(dispatch),
                    onShowSimilarSectionsAction: (payload) => showSimilarSectionsAction(dispatch, payload),
                    onSetSelectSimilarSnackbarVisibility: (payload) =>
                        setSelectSimilarSnackbarVisibilityAction(dispatch, payload),
                    onCurrencyUpdated: (payload) => currencyUpdatedAction(dispatch, payload),
                    onRowSelectAction: (payload) => rowSelectFilterChangeAction(dispatch, payload),
                },
            }}
        >
            {children}
        </Provider>
    )
}

export default filterContext
